import { useCallback, useContext, useMemo } from 'react';
import { Link, useLocation, useOutletContext, useSearchParams } from 'react-router-dom';
import ActivityStatusBadge from '@/modules/common/components/ActivityStatusBadge.tsx';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import { dateToPrint } from '@/modules/common/utils/date-format.ts';
import { formatNumberWithCommas } from '@/modules/common/utils/format-numbers.ts';
import { mappingTypeActivities } from '@/modules/common/utils/type-format.ts';
import { IssuerDashboardWrapperOutletContext } from '@/modules/issuer-dashboard/components/IssuerDashboardWrapper.tsx';
import { IssuerOperationArchiveButton } from '@/modules/issuer-dashboard/components/IssuerOperationArchiveButton.tsx';
import { DividendOperationInfoModal } from '@/modules/issuer-dashboard/components/distribution-dashboard/DividendOperationInfoModal.tsx';
import { IssuerActivitiesOperationInfoModal } from '@/modules/issuer-dashboard/components/modals/IssuerActivitiesOperationInfoModal.tsx';
import {
  IssuerConfirmPaymentModal,
  IssuerConfirmPaymentModalProps,
} from '@/modules/issuer-dashboard/components/modals/IssuerConfirmPaymentModal.tsx';
import {
  IssuerConfirmTransactionModal,
  IssuerConfirmTransactionModalProps,
} from '@/modules/issuer-dashboard/components/modals/IssuerConfirmTransactionModal.tsx';
import {
  IssuerConfirmTransferWithLoadByIdModal,
  IssuerConfirmTransferWithLoadByIdModalProps,
} from '@/modules/issuer-dashboard/components/modals/IssuerConfirmTransferWithLoadByIdModal.tsx';
import { IssuerTransactionsOperationInfoModal } from '@/modules/issuer-dashboard/components/modals/IssuerTransactionsOperationInfoModal.tsx';
import { useBuyBackEventArchiveByIdMutation } from '@/modules/issuer-dashboard/queries/buyBack.tsx';
import {
  usePaymentArchiveByIdMutation,
  usePaymentSetRejectedMutation,
} from '@/modules/issuer-dashboard/queries/payments.tsx';
import {
  useTransactionArchiveByIdMutation,
  useTransactionSetRejectedMutation,
} from '@/modules/issuer-dashboard/queries/transactions.ts';
import {
  useTransferArchiveByIdMutation,
  useTransferSetRejectedMutation,
} from '@/modules/issuer-dashboard/queries/transfers.tsx';
import { issuerDashboardPaths } from '@/modules/issuer-dashboard/routes.tsx';
import {
  Button,
  DeclineModal,
  DeclineModalProps,
  DisclosureListItem,
  ModalProviderContext,
  Tooltip,
} from 'ui';
import { InfoCircleWhiteIcon } from 'ui/icons';
import { fixNameEnum, goodFormatUSDCurrency, goodFormatUSDCurrencyLowPrecision } from 'ui/utils';
import {
  ActivitiesPaginateResponse,
  ActivitiesStatus,
  ActivitiesType,
  EntityStatus,
  PaymentSetRejectedStatusRequest,
  TransactionSetRejectStatusRequest,
  TransactionStatus,
  TransactionType,
  TransferSetRejectedStatusRequest,
} from 'api';

export interface IssuerActivityItemProps extends ActivitiesPaginateResponse {
  investor_name?: string;
  register_id: number;
  index: number;
  isDeleted?: boolean;
  showArchiveButton?: boolean;
}

export const IssuerActivityItem = ({
  id,
  memo,
  type,
  source,
  status,
  amount,
  quantity,
  payment_id,
  register_id,
  payment_amount,
  transaction_id,
  from_entity_id,
  from_entity_name,
  to_entity_id,
  to_entity_name,
  completion_date,
  price_per_share,
  register_class_id,
  requested_at_date,
  payment_dividend_event_id,
  register_class_name,
  total,
  current_price_per_share,
  entity_status,
  index,
  isDeleted,
  showArchiveButton = false,
}: IssuerActivityItemProps) => {
  const { openModal } = useContext(ModalProviderContext);
  const { registerId, registerCountry } = useOutletContext<IssuerDashboardWrapperOutletContext>();
  const [params] = useSearchParams();
  const { pathname } = useLocation();
  const getRegisterTypeStrings = useRegisterTypeStrings();

  const { mutateAsync: rejectTransaction } = useTransactionSetRejectedMutation();
  const { mutateAsync: rejectPayment } = usePaymentSetRejectedMutation();
  const { mutateAsync: rejectTransfer } = useTransferSetRejectedMutation();
  const { mutateAsync: archiveTransaction } = useTransactionArchiveByIdMutation();
  const { mutateAsync: archivePayment } = usePaymentArchiveByIdMutation();
  const { mutateAsync: archiveTransfer } = useTransferArchiveByIdMutation();
  const { mutateAsync: archiveBuyBack } = useBuyBackEventArchiveByIdMutation();

  const handleRejectTransaction = useCallback(
    async (payload: TransactionSetRejectStatusRequest) => {
      await rejectTransaction({ ...payload, id });
    },
    [id, rejectTransaction],
  );

  const handleRejectPayment = useCallback(
    async (payload: PaymentSetRejectedStatusRequest) => {
      await rejectPayment({ ...payload, id: payment_id || 0 });
    },
    [payment_id, rejectPayment],
  );

  const handleRejectTransfer = useCallback(
    async (payload: TransferSetRejectedStatusRequest) => {
      await rejectTransfer({ ...payload, id });
    },
    [id, rejectTransfer],
  );

  const name = useMemo(() => {
    if (type === 'transfer') {
      return (
        <>
          From{' '}
          <Link
            state={{ from: pathname + '?' + params.toString() }}
            to={{
              pathname: issuerDashboardPaths.dynamic.holder(`${from_entity_id}`),
              search: params.toString(),
            }}
            className="text-blue-700 hover:underline"
          >
            {from_entity_name}
          </Link>{' '}
          To{' '}
          <Link
            state={{ from: pathname + '?' + params.toString() }}
            to={{
              pathname: issuerDashboardPaths.dynamic.holder(`${to_entity_id}`),
              search: params.toString(),
            }}
            className="text-blue-700 hover:underline"
          >
            {to_entity_name}
          </Link>
        </>
      );
    }

    if (type === 'subscription') {
      return getRegisterTypeStrings('application');
    }
    return mappingTypeActivities(type);
  }, [
    from_entity_id,
    from_entity_name,
    getRegisterTypeStrings,
    params,
    pathname,
    to_entity_id,
    to_entity_name,
    type,
  ]);

  const pendingStatusActions = useMemo(
    () => (
      <div className="flex items-center justify-start gap-4">
        <Button
          variant="secondary"
          className="w-[90px]"
          onClick={() => {
            if (type === 'transfer') {
              openModal<DeclineModalProps<TransferSetRejectedStatusRequest>>(DeclineModal, {
                onConfirm: handleRejectTransfer,
              });
              return;
            }
            openModal<DeclineModalProps<TransactionSetRejectStatusRequest>>(DeclineModal, {
              onConfirm: handleRejectTransaction,
            });
          }}
        >
          Decline
        </Button>

        <Button
          className="min-w-[90px]"
          onClick={() => {
            if (type === 'transfer') {
              openModal<IssuerConfirmTransferWithLoadByIdModalProps>(
                IssuerConfirmTransferWithLoadByIdModal,
                {
                  transferId: id,
                },
              );
              return;
            }

            openModal<IssuerConfirmTransactionModalProps>(IssuerConfirmTransactionModal, {
              id,
              register_class_id: register_class_id || 0,
              quantity,
              price_per_share: price_per_share || current_price_per_share || 0,
              register_class_name: register_class_name || '',
              register_id,
              status: status,
              type: type,
              total: total,
              issue_date: completion_date,
            });
          }}
        >
          Confirm
        </Button>
      </div>
    ),
    [
      completion_date,
      current_price_per_share,
      handleRejectTransaction,
      handleRejectTransfer,
      id,
      openModal,
      price_per_share,
      quantity,
      register_class_id,
      register_class_name,
      register_id,
      status,
      total,
      type,
    ],
  );

  const awaitingPaymentStatusActions = useMemo(
    () =>
      payment_id && (
        <div className="flex items-center justify-start gap-4">
          <Button
            variant="secondary"
            className="w-[90px]"
            onClick={() =>
              openModal<DeclineModalProps<PaymentSetRejectedStatusRequest>>(DeclineModal, {
                onConfirm: handleRejectPayment,
              })
            }
          >
            Decline
          </Button>
          <Button
            className="min-w-[90px] flex gap-2"
            disabled={entity_status !== EntityStatus.EntityApproved}
            onClick={() =>
              openModal<IssuerConfirmPaymentModalProps>(IssuerConfirmPaymentModal, {
                payment_id,
                price_per_share: price_per_share || 0,
                amount: payment_amount || 0,
                isTransactionCompleted: status === TransactionStatus.Completed,
              })
            }
          >
            <span>Confirm</span>
            {entity_status !== EntityStatus.EntityApproved && (
              <Tooltip
                position={index === 0 ? 'down' : 'up'}
                positionHorizontal="right"
                variant="gray"
                delayTimeOpen={0}
                delayTimeClose={0}
                button={
                  <img
                    alt="info"
                    src={InfoCircleWhiteIcon}
                    className="w-[18px] h-[18px] min-h-[18px] min-w-[18px]"
                  />
                }
              >
                <span>You can't confirm the transaction if Entity is not approved</span>
              </Tooltip>
            )}
          </Button>
        </div>
      ),
    [
      entity_status,
      handleRejectPayment,
      index,
      openModal,
      payment_amount,
      payment_id,
      price_per_share,
    ],
  );

  const handleOpenFullDividend = useCallback(
    (id: number) => () => {
      openModal(DividendOperationInfoModal, {
        id,
        registerId,
        registerCountry,
        isDeleted,
      });
    },
    [isDeleted, openModal, registerCountry, registerId],
  );

  const handleOpenFullPayment = useCallback(() => {
    if (!payment_id) return;
    openModal(IssuerActivitiesOperationInfoModal, {
      paymentId: payment_id,
      registerId,
      registerCountry,
      isDeleted,
    });
  }, [isDeleted, openModal, payment_id, registerCountry, registerId]);

  const handleOpenFullTransaction = useCallback(() => {
    if (!transaction_id) return;
    openModal(IssuerTransactionsOperationInfoModal, {
      id: transaction_id,
      registerId,
      registerCountry,
      isDeleted,
    });
  }, [isDeleted, openModal, registerCountry, registerId, transaction_id]);

  const archiveAction = useMemo(() => {
    if (!showArchiveButton || isDeleted) return null;

    if (source === 'payment') {
      return payment_id
        ? {
            id: payment_id,
            entityName: 'payment',
            onArchive: archivePayment,
          }
        : null;
    }

    if (source === 'transfer' || type === ActivitiesType.Transfer) {
      return {
        id,
        entityName: 'transfer',
        onArchive: archiveTransfer,
      };
    }

    if (type === TransactionType.BuyBack || source === 'buy_back') {
      return {
        id,
        entityName: 'buy back event',
        onArchive: archiveBuyBack,
      };
    }

    return {
      id,
      entityName: 'transaction',
      onArchive: archiveTransaction,
    };
  }, [
    archiveBuyBack,
    archivePayment,
    archiveTransaction,
    archiveTransfer,
    id,
    isDeleted,
    payment_id,
    showArchiveButton,
    source,
    type,
  ]);

  const bottomContentData = useMemo<Array<[JSX.Element | string, JSX.Element | string]>>(() => {
    const info: Array<[JSX.Element | string, JSX.Element | string]> = [];
    if (requested_at_date) {
      info.push(['Request date', dateToPrint(requested_at_date) || '-']);
    }
    if (completion_date) {
      info.push(['Completion date', dateToPrint(completion_date) || '-']);
    }
    if (register_class_name && register_class_id) {
      info.push([
        'Class',
        <Link
          state={{ from: pathname + '?' + params.toString() }}
          to={{
            pathname: issuerDashboardPaths.dynamic.classById(register_class_id.toString()),
            search: params.toString(),
          }}
          className="text-blue-700 hover:underline"
        >
          {register_class_name}
        </Link>,
      ]);
    }
    if (from_entity_id && from_entity_name) {
      info.push([
        'From investor',
        <Link
          state={{ from: pathname + '?' + params.toString() }}
          to={{
            pathname: issuerDashboardPaths.dynamic.holder(from_entity_id.toString()),
            search: params.toString(),
          }}
          className="text-blue-700 hover:underline"
        >
          {from_entity_name}
        </Link>,
      ]);
    }

    if (to_entity_id && to_entity_name) {
      info.push([
        'To investor',
        <Link
          state={{ from: pathname + '?' + params.toString() }}
          to={{
            pathname: issuerDashboardPaths.dynamic.holder(to_entity_id.toString()),
            search: params.toString(),
          }}
          className="text-blue-700 hover:underline"
        >
          {to_entity_name}
        </Link>,
      ]);
    }
    if (price_per_share) {
      info.push(['Price per share/unit', goodFormatUSDCurrency(price_per_share) || '-']);
    }
    if (payment_id) {
      info.push([
        'Payment ID',
        <span
          className="text-sm leading-[22px] text-[#0369A1] font-[500] cursor-pointer hover:underline"
          onClick={handleOpenFullPayment}
        >
          #{payment_id}
        </span>,
      ]);
    }
    if (transaction_id) {
      info.push([
        'Transaction ID',
        <span
          className="text-sm leading-[22px] text-[#0369A1] font-[500] cursor-pointer hover:underline"
          onClick={handleOpenFullTransaction}
        >
          #{transaction_id}
        </span>,
      ]);
    }
    if (payment_dividend_event_id) {
      info.push([
        `${getRegisterTypeStrings('dividend', { capitalize: true })} event ID`,
        <span
          className="text-sm leading-[22px] text-[#0369A1] font-[500] cursor-pointer hover:underline"
          onClick={handleOpenFullDividend(payment_dividend_event_id)}
        >
          #{payment_dividend_event_id}
        </span>,
      ]);
    }
    if (memo) {
      info.push(['Memo', memo]);
    }

    if (
      !isDeleted &&
      status === ActivitiesStatus.Pending &&
      source !== 'payment' &&
      !!pendingStatusActions
    ) {
      info.push([<div>{pendingStatusActions}</div>, <></>]);
    }

    if (
      !isDeleted &&
      !!awaitingPaymentStatusActions &&
      ((status === ActivitiesStatus.AwaitingPayment && type !== 'transfer' && !!payment_id) ||
        (source === 'payment' && status === ActivitiesStatus.Pending && !!payment_id))
    ) {
      info.push([<div>{awaitingPaymentStatusActions}</div>, <></>]);
    }

    return info;
  }, [
    requested_at_date,
    completion_date,
    register_class_name,
    register_class_id,
    from_entity_id,
    from_entity_name,
    to_entity_id,
    to_entity_name,
    price_per_share,
    payment_id,
    transaction_id,
    payment_dividend_event_id,
    memo,
    isDeleted,
    pathname,
    params,
    handleOpenFullPayment,
    handleOpenFullTransaction,
    getRegisterTypeStrings,
    handleOpenFullDividend,
    status,
    source,
    pendingStatusActions,
    type,
    awaitingPaymentStatusActions,
  ]);

  const calculateRightMainString = (): string => {
    if (type === TransactionType.Drp && total) {
      if (quantity && price_per_share) {
        return goodFormatUSDCurrencyLowPrecision(quantity * price_per_share);
      }
      return goodFormatUSDCurrencyLowPrecision(total);
    }

    return goodFormatUSDCurrencyLowPrecision(amount);
  };
  return (
    <DisclosureListItem
      responsiveContent
      leftMainString={name}
      leftSubduedContentSet={[
        completion_date
          ? `Completed at: ${dateToPrint(completion_date)}`
          : `Requested at: ${dateToPrint(requested_at_date)}`,
        register_class_name || '-',
        fixNameEnum(source),
      ].filter(item => item !== '-')}
      classes={{
        rightMainString: 'w-auto max-w-full break-words text-right [overflow-wrap:anywhere]',
        rightContentUp: 'justify-items-end',
        rightContent: !isDeleted && archiveAction ? 'gap-1 w-[80px] shrink-0' : undefined,
      }}
      rightContent={
        <span className="flex gap-4">
          {payment_dividend_event_id && (
            <span
              className={
                'text-[12px] leading-[20px] text-white bg-blue-400 w-fit select-none ' +
                'rounded-[1000px] px-2 py-[2px] capitalize col-start-1 col-end-2'
              }
            >
              {getRegisterTypeStrings('dividend', { capitalize: true })}
            </span>
          )}
          <ActivityStatusBadge status={status || ''} />
        </span>
      }
      rightAdditionalContent={
        !isDeleted && archiveAction ? (
          <IssuerOperationArchiveButton
            id={archiveAction.id}
            entityName={archiveAction.entityName}
            onArchive={archiveAction.onArchive}
          />
        ) : null
      }
      rightMainString={calculateRightMainString()}
      rightSubduedString={
        source === 'payment' ||
        (type === TransactionType.Drp &&
          (status === TransactionStatus.Pending ||
            status === TransactionStatus.DividendPending ||
            TransactionStatus.Unsuccessfull) &&
          total &&
          !quantity)
          ? undefined
          : `${formatNumberWithCommas(+quantity.toFixed(4))} units/shares`
      }
      bottomContent={bottomContentData}
    />
  );
};
